import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { ISitesMetadataDictionary } from "../../sites/SiteModels";
import { getSitesMetadataAsync } from "../../sites/SiteAPI";
import { ITagMetadata } from "../../data_point/models/TagsModels";
import { addAliasedTags, getSiteTagsMetadata } from "../TagsMetadataUtils";
import { LoadingState } from "../LoadingState";
import { RootState } from "../Store";

interface ISitesMetadataStore {
  loading: LoadingState;
  sites: ISitesMetadataDictionary;
  siteCodeToIdMap: { [key: string]: string };
  error?: unknown;
  activeRequestId?: string;
  loadingBeforeRequest?: LoadingState;
}

export const defaultProperties: ISitesMetadataStore = {
  loading: LoadingState.None,
  sites: {},
  siteCodeToIdMap: {},
};

export const sitesMetadataSlice = createSlice({
  name: "multiSitesMetadata",
  initialState: defaultProperties,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchSitesMetadata.pending, (state, action) => {
        if (state.loading !== LoadingState.Pending) {
          state.loadingBeforeRequest = state.loading;
        }
        state.activeRequestId = action.meta.requestId;
        state.loading = LoadingState.Pending;
        state.error = undefined;
      })
      .addCase(fetchSitesMetadata.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) {
          return;
        }
        delete state.activeRequestId;
        state.sites = action.payload;
        state.siteCodeToIdMap = {};
        for (const [siteId, siteMetadata] of Object.entries(state.sites)) {
          state.siteCodeToIdMap[siteMetadata.siteCode] = siteId;
        }

        for (const [siteId, siteMetadata] of Object.entries(state.sites)) {
          const metadata = getSiteTagsMetadata(siteMetadata);
          state.sites[siteId].tags = metadata.reduce(
            (acc, tag) => {
              acc[tag.uniqueName] = tag;
              return acc;
            },
            {} as { [key: string]: ITagMetadata }
          );
        }
        addAliasedTags(state.sites);
        state.loading = LoadingState.Complete;
        state.error = undefined;
      })
      .addCase(fetchSitesMetadata.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) {
          return;
        }
        delete state.activeRequestId;
        state.error = action.meta.aborted ? undefined : action.error;
        state.loading = action.meta.aborted
          ? (state.loadingBeforeRequest ?? LoadingState.None)
          : LoadingState.Error;
      }),
});

export const fetchSitesMetadata = createAsyncThunk(
  "multiSitesMetadata/fetchSitesMetadata",
  (siteIds: string[]) => getSitesMetadataAsync(siteIds)
);

export const selectSiteCodeToIdMap = (state: RootState) => state.multiSitesMetadata.siteCodeToIdMap;

export const sitesMetadataReducer = sitesMetadataSlice.reducer;
